import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  Battery, Zap, Shield, Cpu, ArrowLeft, ArrowRight, Info,
  CircuitBoard, Layers, Grid3X3, CheckCircle, AlertCircle,
  Phone, MessageCircle, Calculator, Edit3
} from "lucide-react";
import { Button } from "./components/ui/button";
import { Input } from "./components/ui/input";
import { Label } from "./components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./components/ui/select";

// Logo URL
const LOGO_URL = "https://customer-assets.emergentagent.com/job_energy-havolt/artifacts/x68m0xml_Final%20LOGO.png";

// LiFePO4 Cell Constants
const CELL_VOLTAGE = 3.2; // Nominal voltage per cell
const CELL_VOLTAGE_MAX = 3.65; // Max charge voltage
const CELL_VOLTAGE_MIN = 2.5; // Min discharge voltage

// Standard cell capacity options (Ah)
const CELL_CAPACITY_OPTIONS = [6, 10, 15, 20, 50, 100, 150, 200, 280, 304];

// Standard voltage options
const VOLTAGE_OPTIONS = [12, 24, 36, 48, 60, 72, 84, 96];

// Ah options for quick select
const AH_OPTIONS = [15, 30, 50, 60, 75, 100, 120, 150, 200, 280, 300, 400, 500];

const BatteryConfigurator = () => {
  const [selectedVoltage, setSelectedVoltage] = useState(null);
  const [selectedAh, setSelectedAh] = useState(null);
  const [cellCapacity, setCellCapacity] = useState(15); // Default 15Ah cells
  const [customCellCapacity, setCustomCellCapacity] = useState("");
  const [customVoltage, setCustomVoltage] = useState("");
  const [customAh, setCustomAh] = useState("");
  const [useCustom, setUseCustom] = useState(false);
  const [configuration, setConfiguration] = useState(null);

  // Get effective cell capacity
  const getEffectiveCellCapacity = () => {
    if (customCellCapacity && parseFloat(customCellCapacity) > 0) {
      return parseFloat(customCellCapacity);
    }
    return cellCapacity;
  };

  // Calculate configuration when voltage or Ah changes
  useEffect(() => {
    const voltage = useCustom ? parseFloat(customVoltage) : selectedVoltage;
    const ah = useCustom ? parseFloat(customAh) : selectedAh;
    const effectiveCellCapacity = getEffectiveCellCapacity();
    
    if (voltage && ah && voltage > 0 && ah > 0 && effectiveCellCapacity > 0) {
      calculateConfiguration(voltage, ah, effectiveCellCapacity);
    } else {
      setConfiguration(null);
    }
  }, [selectedVoltage, selectedAh, customVoltage, customAh, useCustom, cellCapacity, customCellCapacity]);

  const calculateConfiguration = (voltage, ah, singleCellCapacity) => {
    // Cells in series for voltage
    const cellsInSeries = Math.ceil(voltage / CELL_VOLTAGE);
    const actualVoltage = parseFloat((cellsInSeries * CELL_VOLTAGE).toFixed(1));

    // Cells in parallel for capacity
    const cellsInParallel = Math.ceil(ah / singleCellCapacity);
    const actualCapacity = cellsInParallel * singleCellCapacity;

    // Total cells
    const totalCells = cellsInSeries * cellsInParallel;

    // BMS Configuration
    const bmsConfig = `${cellsInSeries}S${cellsInParallel}P`;
    
    // Energy calculation
    const energyWh = actualVoltage * actualCapacity;
    const energyKwh = parseFloat((energyWh / 1000).toFixed(2));

    // Max and Min voltage of pack
    const maxPackVoltage = parseFloat((cellsInSeries * CELL_VOLTAGE_MAX).toFixed(1));
    const minPackVoltage = parseFloat((cellsInSeries * CELL_VOLTAGE_MIN).toFixed(1));

    // Recommended BMS current rating (1C rate)
    const bmsCurrent = actualCapacity;
    const peakCurrent = actualCapacity * 2; // 2C peak

    // Estimated weight (approx based on cell capacity)
    // Rough estimation: ~0.033kg per Ah for cylindrical cells
    const cellWeight = singleCellCapacity * 0.033;
    const totalWeight = parseFloat((totalCells * cellWeight).toFixed(1));

    setConfiguration({
      cellsInSeries,
      cellsInParallel,
      totalCells,
      actualVoltage,
      actualCapacity,
      bmsConfig,
      energyWh,
      energyKwh,
      maxPackVoltage,
      minPackVoltage,
      bmsCurrent,
      peakCurrent,
      totalWeight,
      cellCapacityUsed: singleCellCapacity,
    });
  };

  const resetConfiguration = () => {
    setSelectedVoltage(null);
    setSelectedAh(null);
    setCustomVoltage("");
    setCustomAh("");
    setUseCustom(false);
    setConfiguration(null);
  };

  const handleVoltageSelect = (v) => {
    setSelectedVoltage(v);
    setUseCustom(false);
    setCustomVoltage("");
  };

  const handleAhSelect = (ah) => {
    setSelectedAh(ah);
    setUseCustom(false);
    setCustomAh("");
  };

  const handleCustomInput = () => {
    setUseCustom(true);
    setSelectedVoltage(null);
    setSelectedAh(null);
  };

  return (
    <div className="min-h-screen bg-[#09090B]">
      {/* Header */}
      <header className="fixed top-0 w-full z-50 bg-black/90 backdrop-blur-md border-b border-zinc-800">
        <div className="container mx-auto px-4 md:px-8">
          <div className="flex items-center justify-between h-20">
            <Link to="/" className="flex items-center gap-3">
              <img src={LOGO_URL} alt="Havolt" className="h-12 w-auto" />
            </Link>
            <div className="flex items-center gap-4">
              <Link to="/">
                <Button variant="outline" className="border-zinc-700 text-zinc-300 hover:border-[#18C964] hover:text-[#18C964]">
                  <ArrowLeft className="mr-2 h-4 w-4" />
                  Back to Home
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="pt-32 pb-20">
        <div className="container mx-auto px-4 md:px-8">
          {/* Page Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-12"
          >
            <span className="inline-flex items-center gap-2 px-4 py-2 mb-6 border border-zinc-800 text-xs font-mono uppercase tracking-widest text-zinc-400">
              <Calculator className="h-4 w-4 text-[#18C964]" />
              Battery Configurator
            </span>
            <h1 className="text-4xl md:text-6xl font-heading font-bold uppercase tracking-tight mb-4">
              Design Your <span className="text-[#18C964]">Battery Pack</span>
            </h1>
            <p className="text-zinc-400 max-w-2xl mx-auto text-lg">
              Select your desired voltage and capacity to see the complete cell configuration, 
              BMS requirements, and technical specifications.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 max-w-6xl mx-auto">
            {/* Configuration Panel */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              className="bg-havolt-paper/50 border border-zinc-800 p-8"
            >
              <h2 className="text-xl font-heading font-bold uppercase mb-6 flex items-center gap-2">
                <Settings className="h-5 w-5 text-[#18C964]" />
                Select Configuration
              </h2>

              {/* Voltage Selection */}
              <div className="mb-8">
                <Label className="text-xs font-mono uppercase tracking-widest text-zinc-500 mb-3 block">
                  Desired Voltage (V)
                </Label>
                <div className="grid grid-cols-4 gap-2">
                  {VOLTAGE_OPTIONS.map((v) => (
                    <button
                      key={v}
                      onClick={() => handleVoltageSelect(v)}
                      className={`
                        p-4 border text-center font-mono font-bold transition-all
                        ${selectedVoltage === v && !useCustom
                          ? 'bg-[#18C964]/20 border-[#18C964] text-[#18C964]' 
                          : 'bg-havolt-paper border-zinc-700 text-zinc-300 hover:border-zinc-500'
                        }
                      `}
                      data-testid={`voltage-${v}`}
                    >
                      {v}V
                    </button>
                  ))}
                </div>
              </div>

              {/* Capacity Selection */}
              <div className="mb-8">
                <Label className="text-xs font-mono uppercase tracking-widest text-zinc-500 mb-3 block">
                  Desired Capacity (Ah)
                </Label>
                <div className="grid grid-cols-4 sm:grid-cols-5 gap-2">
                  {AH_OPTIONS.map((ah) => (
                    <button
                      key={ah}
                      onClick={() => handleAhSelect(ah)}
                      className={`
                        p-3 border text-center font-mono font-bold transition-all text-sm
                        ${selectedAh === ah && !useCustom
                          ? 'bg-[#18C964]/20 border-[#18C964] text-[#18C964]' 
                          : 'bg-havolt-paper border-zinc-700 text-zinc-300 hover:border-zinc-500'
                        }
                      `}
                      data-testid={`ah-${ah}`}
                    >
                      {ah}Ah
                    </button>
                  ))}
                </div>
              </div>

              {/* Cell Capacity Selection */}
              <div className="mb-8">
                <Label className="text-xs font-mono uppercase tracking-widest text-zinc-500 mb-3 block">
                  Cell Capacity (Ah per cell)
                </Label>
                <div className="grid grid-cols-5 gap-2 mb-3">
                  {CELL_CAPACITY_OPTIONS.map((cap) => (
                    <button
                      key={cap}
                      onClick={() => {
                        setCellCapacity(cap);
                        setCustomCellCapacity("");
                      }}
                      className={`
                        p-3 border text-center font-mono font-bold transition-all text-sm
                        ${cellCapacity === cap && !customCellCapacity
                          ? 'bg-[#18C964]/20 border-[#18C964] text-[#18C964]' 
                          : 'bg-havolt-paper border-zinc-700 text-zinc-300 hover:border-zinc-500'
                        }
                      `}
                      data-testid={`cell-cap-${cap}`}
                    >
                      {cap}Ah
                    </button>
                  ))}
                </div>
                <div className="flex items-center gap-2">
                  <Input
                    type="number"
                    placeholder="Custom cell Ah"
                    value={customCellCapacity}
                    onChange={(e) => setCustomCellCapacity(e.target.value)}
                    className={`bg-havolt-paper border-zinc-700 h-10 font-mono flex-1 ${customCellCapacity ? 'border-[#18C964]' : ''}`}
                    data-testid="custom-cell-capacity"
                  />
                  <span className="text-xs text-zinc-500">Ah</span>
                </div>
              </div>

              {/* Custom Input Section */}
              <div className="mb-8">
                <div className="flex items-center justify-between mb-3">
                  <Label className="text-xs font-mono uppercase tracking-widest text-zinc-500">
                    Or Enter Custom Values
                  </Label>
                  <button
                    onClick={handleCustomInput}
                    className={`text-xs flex items-center gap-1 ${useCustom ? 'text-[#18C964]' : 'text-zinc-500 hover:text-zinc-300'}`}
                  >
                    <Edit3 className="h-3 w-3" />
                    Custom
                  </button>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Input
                      type="number"
                      placeholder="Voltage (V)"
                      value={customVoltage}
                      onChange={(e) => {
                        setCustomVoltage(e.target.value);
                        if (e.target.value) {
                          setUseCustom(true);
                          setSelectedVoltage(null);
                        }
                      }}
                      className={`bg-havolt-paper border-zinc-700 h-12 font-mono ${useCustom && customVoltage ? 'border-[#18C964]' : ''}`}
                      data-testid="custom-voltage"
                    />
                  </div>
                  <div>
                    <Input
                      type="number"
                      placeholder="Capacity (Ah)"
                      value={customAh}
                      onChange={(e) => {
                        setCustomAh(e.target.value);
                        if (e.target.value) {
                          setUseCustom(true);
                          setSelectedAh(null);
                        }
                      }}
                      className={`bg-havolt-paper border-zinc-700 h-12 font-mono ${useCustom && customAh ? 'border-[#18C964]' : ''}`}
                      data-testid="custom-ah"
                    />
                  </div>
                </div>
              </div>

              {/* Info Box */}
              <div className="p-4 bg-zinc-800/50 border border-zinc-700 flex items-start gap-3">
                <Info className="h-5 w-5 text-[#18C964] flex-shrink-0 mt-0.5" />
                <div className="text-sm text-zinc-400">
                  <p className="font-medium text-zinc-300 mb-1">LiFePO4 Cell Specifications</p>
                  <ul className="space-y-1">
                    <li>• Cell Configuration: 3.2V {getEffectiveCellCapacity()}Ah</li>
                    <li>• Max Charge: 3.65V per cell</li>
                    <li>• Min Discharge: 2.5V per cell</li>
                    <li>• Cycle Life: 2000-6000+ cycles</li>
                  </ul>
                </div>
              </div>

              {/* Reset Button */}
              {configuration && (
                <Button
                  onClick={resetConfiguration}
                  variant="outline"
                  className="w-full mt-6 border-zinc-700 text-zinc-400 hover:border-red-500 hover:text-red-500"
                >
                  Reset Configuration
                </Button>
              )}
            </motion.div>

            {/* Results Panel */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              className="bg-havolt-paper/50 border border-zinc-800 p-8"
            >
              <h2 className="text-xl font-heading font-bold uppercase mb-6 flex items-center gap-2">
                <CircuitBoard className="h-5 w-5 text-[#18C964]" />
                Battery Configuration
              </h2>

              {configuration ? (
                <div className="space-y-6">
                  {/* Visual Battery Representation */}
                  <div className="bg-zinc-800/50 border border-zinc-700 p-6 text-center">
                    <div className="inline-flex items-center gap-4 mb-4">
                      <Battery className="h-16 w-16 text-[#18C964]" />
                      <div className="text-left">
                        <div className="text-3xl font-heading font-bold text-[#18C964]">
                          {configuration.actualVoltage}V / {configuration.actualCapacity}Ah
                        </div>
                        <div className="text-sm text-zinc-400">
                          {configuration.energyKwh} kWh Energy Storage
                        </div>
                      </div>
                    </div>
                    <div className="text-4xl font-heading font-bold text-white">
                      {configuration.bmsConfig}
                    </div>
                    <div className="text-xs font-mono text-zinc-500 mt-1">
                      BMS CONFIGURATION
                    </div>
                  </div>

                  {/* Cell Configuration */}
                  <div className="grid grid-cols-3 gap-4">
                    <div className="bg-zinc-800/30 border border-zinc-700 p-4 text-center">
                      <Layers className="h-6 w-6 text-[#18C964] mx-auto mb-2" />
                      <div className="text-2xl font-bold text-white">{configuration.cellsInSeries}</div>
                      <div className="text-xs font-mono text-zinc-500">CELLS IN SERIES</div>
                      <div className="text-xs text-zinc-400 mt-1">(for voltage)</div>
                    </div>
                    <div className="bg-zinc-800/30 border border-zinc-700 p-4 text-center">
                      <Grid3X3 className="h-6 w-6 text-[#18C964] mx-auto mb-2" />
                      <div className="text-2xl font-bold text-white">{configuration.cellsInParallel}</div>
                      <div className="text-xs font-mono text-zinc-500">CELLS IN PARALLEL</div>
                      <div className="text-xs text-zinc-400 mt-1">(for capacity)</div>
                    </div>
                    <div className="bg-zinc-800/30 border border-zinc-700 p-4 text-center">
                      <Battery className="h-6 w-6 text-[#18C964] mx-auto mb-2" />
                      <div className="text-2xl font-bold text-white">{configuration.totalCells}</div>
                      <div className="text-xs font-mono text-zinc-500">TOTAL CELLS</div>
                      <div className="text-xs text-zinc-400 mt-1">(3.2V {configuration.cellCapacityUsed}Ah each)</div>
                    </div>
                  </div>

                  {/* Technical Specifications */}
                  <div className="space-y-3">
                    <h3 className="text-sm font-mono uppercase tracking-widest text-zinc-500">
                      Technical Specifications
                    </h3>
                    
                    <div className="grid grid-cols-2 gap-3">
                      <div className="flex justify-between p-3 bg-zinc-800/30 border border-zinc-700">
                        <span className="text-zinc-400">Nominal Voltage</span>
                        <span className="font-mono text-[#18C964]">{configuration.actualVoltage}V</span>
                      </div>
                      <div className="flex justify-between p-3 bg-zinc-800/30 border border-zinc-700">
                        <span className="text-zinc-400">Max Voltage</span>
                        <span className="font-mono text-[#18C964]">{configuration.maxPackVoltage}V</span>
                      </div>
                      <div className="flex justify-between p-3 bg-zinc-800/30 border border-zinc-700">
                        <span className="text-zinc-400">Min Voltage</span>
                        <span className="font-mono text-[#18C964]">{configuration.minPackVoltage}V</span>
                      </div>
                      <div className="flex justify-between p-3 bg-zinc-800/30 border border-zinc-700">
                        <span className="text-zinc-400">Capacity</span>
                        <span className="font-mono text-[#18C964]">{configuration.actualCapacity}Ah</span>
                      </div>
                      <div className="flex justify-between p-3 bg-zinc-800/30 border border-zinc-700">
                        <span className="text-zinc-400">Energy</span>
                        <span className="font-mono text-[#18C964]">{configuration.energyKwh} kWh</span>
                      </div>
                      <div className="flex justify-between p-3 bg-zinc-800/30 border border-zinc-700">
                        <span className="text-zinc-400">Est. Weight</span>
                        <span className="font-mono text-[#18C964]">~{configuration.totalWeight} kg</span>
                      </div>
                    </div>
                  </div>

                  {/* BMS Requirements */}
                  <div className="space-y-3">
                    <h3 className="text-sm font-mono uppercase tracking-widest text-zinc-500">
                      BMS Requirements
                    </h3>
                    
                    <div className="grid grid-cols-1 gap-3">
                      <div className="flex justify-between p-3 bg-zinc-800/30 border border-zinc-700">
                        <span className="text-zinc-400">BMS Type</span>
                        <span className="font-mono text-[#18C964]">{configuration.cellsInSeries}S Smart BMS</span>
                      </div>
                      <div className="flex justify-between p-3 bg-zinc-800/30 border border-zinc-700">
                        <span className="text-zinc-400">Continuous Current</span>
                        <span className="font-mono text-[#18C964]">{configuration.bmsCurrent}A (1C)</span>
                      </div>
                      <div className="flex justify-between p-3 bg-zinc-800/30 border border-zinc-700">
                        <span className="text-zinc-400">Peak Current</span>
                        <span className="font-mono text-[#18C964]">{configuration.peakCurrent}A (2C)</span>
                      </div>
                    </div>
                  </div>

                  {/* BMS Features */}
                  <div className="p-4 bg-[#18C964]/10 border border-[#18C964]/30">
                    <h4 className="font-bold text-[#18C964] mb-2 flex items-center gap-2">
                      <Shield className="h-4 w-4" />
                      Havolt BMS Protection Features
                    </h4>
                    <div className="grid grid-cols-2 gap-2 text-sm text-zinc-300">
                      <div className="flex items-center gap-2">
                        <CheckCircle className="h-4 w-4 text-[#18C964]" />
                        Over-voltage protection
                      </div>
                      <div className="flex items-center gap-2">
                        <CheckCircle className="h-4 w-4 text-[#18C964]" />
                        Under-voltage protection
                      </div>
                      <div className="flex items-center gap-2">
                        <CheckCircle className="h-4 w-4 text-[#18C964]" />
                        Over-current protection
                      </div>
                      <div className="flex items-center gap-2">
                        <CheckCircle className="h-4 w-4 text-[#18C964]" />
                        Short circuit protection
                      </div>
                      <div className="flex items-center gap-2">
                        <CheckCircle className="h-4 w-4 text-[#18C964]" />
                        Temperature protection
                      </div>
                      <div className="flex items-center gap-2">
                        <CheckCircle className="h-4 w-4 text-[#18C964]" />
                        Cell balancing
                      </div>
                    </div>
                  </div>

                  {/* CTA */}
                  <div className="flex flex-col sm:flex-row gap-3 pt-4">
                    <a
                      href={`https://wa.me/919079059738?text=Hi, I need a ${configuration.actualVoltage}V ${configuration.actualCapacity}Ah LiFePO4 battery pack (${configuration.bmsConfig} configuration). Please share pricing.`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex-1"
                    >
                      <Button className="w-full bg-[#25D366] hover:bg-[#20BD5A] text-white font-bold py-4">
                        <MessageCircle className="mr-2 h-5 w-5" />
                        Get Quote on WhatsApp
                      </Button>
                    </a>
                    <a href="tel:+919079059738" className="flex-1">
                      <Button variant="outline" className="w-full border-zinc-700 text-zinc-300 hover:border-[#18C964] hover:text-[#18C964] py-4">
                        <Phone className="mr-2 h-5 w-5" />
                        Call Us
                      </Button>
                    </a>
                  </div>
                </div>
              ) : (
                // Empty State
                <div className="flex flex-col items-center justify-center py-16 text-center">
                  <div className="w-24 h-24 bg-zinc-800 rounded-full flex items-center justify-center mb-6">
                    <Battery className="h-12 w-12 text-zinc-600" />
                  </div>
                  <h3 className="text-xl font-heading font-bold text-zinc-400 mb-2">
                    Select Your Configuration
                  </h3>
                  <p className="text-zinc-500 max-w-sm">
                    Choose your desired voltage and capacity from the options on the left, or enter custom values to see the complete battery configuration.
                  </p>
                </div>
              )}
            </motion.div>
          </div>

          {/* Additional Info Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="mt-16 max-w-6xl mx-auto"
          >
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-havolt-paper/50 border border-zinc-800 p-6 text-center">
                <Zap className="h-10 w-10 text-[#18C964] mx-auto mb-4" />
                <h3 className="font-heading font-bold uppercase mb-2">Custom Voltages</h3>
                <p className="text-sm text-zinc-400">
                  Need a voltage not listed? Enter custom values for any requirement.
                </p>
              </div>
              <div className="bg-havolt-paper/50 border border-zinc-800 p-6 text-center">
                <Shield className="h-10 w-10 text-[#18C964] mx-auto mb-4" />
                <h3 className="font-heading font-bold uppercase mb-2">Advanced BMS</h3>
                <p className="text-sm text-zinc-400">
                  All packs include smart BMS with Bluetooth monitoring and full protection suite.
                </p>
              </div>
              <div className="bg-havolt-paper/50 border border-zinc-800 p-6 text-center">
                <Cpu className="h-10 w-10 text-[#18C964] mx-auto mb-4" />
                <h3 className="font-heading font-bold uppercase mb-2">Grade A+ Cells</h3>
                <p className="text-sm text-zinc-400">
                  We use only premium LiFePO4 cells with matched capacity and internal resistance.
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </main>

      {/* Footer */}
      <footer className="py-8 border-t border-zinc-800">
        <div className="container mx-auto px-4 md:px-8 text-center">
          <p className="text-sm text-zinc-500">
            © 2024 Hari Om Enterprises. All rights reserved. |{" "}
            <a href="https://havoltbattery.com" className="text-[#18C964] hover:underline">
              havoltbattery.com
            </a>
          </p>
        </div>
      </footer>

      {/* WhatsApp Float */}
      <a
        href="https://wa.me/919079059738?text=Hi, I'm interested in custom battery configuration"
        target="_blank"
        rel="noopener noreferrer"
        className="fixed bottom-6 right-6 w-14 h-14 bg-[#25D366] rounded-full flex items-center justify-center shadow-lg hover:scale-110 transition-transform z-50"
        aria-label="Chat on WhatsApp"
      >
        <MessageCircle className="h-7 w-7 text-white" />
      </a>
    </div>
  );
};

// Settings icon component for use in the page
const Settings = ({ className }) => (
  <svg className={className} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z"/>
    <circle cx="12" cy="12" r="3"/>
  </svg>
);

export default BatteryConfigurator;
